import { css } from 'lit';

export const agentStagesStyles = css`
  :host {
    display: block;
    margin: 12px 0;

    /* Default (Explore) mode palette. Other modes override via
       :host([data-mode='analyze' | 'deep-research' | 'auto']) below. */
    --cur-mode-fg: #1e3a8a;
    --cur-mode-bg: rgba(30, 58, 138, 0.08);
    --cur-mode-bg-strong: rgba(30, 58, 138, 0.16);
    --cur-mode-soft-bg: rgba(30, 58, 138, 0.04);
    --cur-mode-ring: rgba(30, 58, 138, 0.4);
  }
  :host([data-mode='analyze']) {
    --cur-mode-fg: #0d9488;
    --cur-mode-bg: rgba(13, 148, 136, 0.08);
    --cur-mode-bg-strong: rgba(13, 148, 136, 0.16);
    --cur-mode-soft-bg: rgba(13, 148, 136, 0.04);
    --cur-mode-ring: rgba(13, 148, 136, 0.4);
  }
  :host([data-mode='deep-research']) {
    --cur-mode-fg: #7c3aed;
    --cur-mode-bg: rgba(124, 58, 237, 0.08);
    --cur-mode-bg-strong: rgba(124, 58, 237, 0.16);
    --cur-mode-soft-bg: rgba(124, 58, 237, 0.04);
    --cur-mode-ring: rgba(124, 58, 237, 0.4);
  }

  /* Transcript flow: no outer card chrome. Each step is a self-contained
     row that sits in the document like a paragraph would. */
  .transcript {
    display: flex;
    flex-direction: column;
    gap: 12px;
    font-family: var(--affine-font-sans-family, system-ui, sans-serif);
    font-size: 14px;
    line-height: 1.5;
    color: var(--affine-text-primary-color);
    animation: curie-transcript-in 240ms ease-out;
  }
  @keyframes curie-transcript-in {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* The tiny mode tag at the top — small, low-contrast, easy to ignore
     once the user knows what they're looking at. */
  .mode-tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    align-self: flex-start;
    padding: 2px 10px;
    background: var(--cur-mode-bg);
    color: var(--cur-mode-fg);
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    border: none;
    font-family: inherit;
  }
  .mode-tag .elapsed {
    color: var(--cur-mode-fg);
    opacity: 0.7;
    font-family: ui-monospace, 'SF Mono', Menlo, monospace;
    font-size: 10px;
    margin-left: 2px;
    font-weight: 500;
  }
  /* Mode pill is interactive once the run is finished — clicking it
     collapses the transcript back into the one-line log. */
  .mode-tag--toggle {
    cursor: pointer;
    transition:
      background 140ms ease,
      box-shadow 140ms ease;
  }
  .mode-tag--toggle:hover {
    background: var(--cur-mode-bg-strong);
  }
  .mode-tag-chevron {
    display: inline-flex;
    align-items: center;
    opacity: 0.7;
  }
  .mode-tag-chevron svg {
    width: 10px;
    height: 10px;
  }

  /* Secondary collapse affordance: text-link at the foot of the
     transcript. Some users look at the top for a toggle, others at
     the bottom — give them both. */
  .transcript-collapse {
    align-self: flex-start;
    margin-top: 6px;
    padding: 0;
    background: transparent;
    border: none;
    cursor: pointer;
    font-family: inherit;
    font-size: 12px;
    color: var(--affine-text-secondary-color);
    text-decoration: underline;
    text-underline-offset: 2px;
    transition: color 140ms ease;
  }
  .transcript-collapse:hover {
    color: var(--cur-mode-fg);
  }

  /* Plan sub-questions ride above the steps when deep research is on. */
  .plan {
    padding: 10px 14px;
    background: var(--cur-mode-soft-bg);
    border-radius: 10px;
    font-size: 13px;
  }
  .plan-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    color: var(--cur-mode-fg);
    font-size: 12px;
    margin-bottom: 4px;
  }
  .plan-title svg {
    width: 12px;
    height: 12px;
  }
  .plan-list {
    margin: 0;
    padding-left: 22px;
  }
  .plan-list li {
    padding: 2px 0;
    color: var(--affine-text-primary-color);
  }

  /* Each step is rendered as a compact inline tool-card. Icon + title
     row, then a sub-step / sources / done row beneath. */
  .step {
    display: flex;
    flex-direction: column;
    gap: 6px;
    animation: curie-step-in 200ms ease-out;
  }
  @keyframes curie-step-in {
    from {
      opacity: 0;
      transform: translateY(-2px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .step--pending {
    opacity: 0.55;
  }

  .step-title-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
  .step-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    background: var(--affine-hover-color, rgba(0, 0, 0, 0.04));
    color: var(--affine-text-secondary-color);
    flex-shrink: 0;
    transition:
      background 140ms ease,
      color 140ms ease;
  }
  .step-icon svg {
    width: 13px;
    height: 13px;
  }
  .step--active .step-icon {
    background: var(--cur-mode-bg);
    color: var(--cur-mode-fg);
  }
  .step-title {
    font-weight: 500;
    color: var(--affine-text-primary-color);
  }
  .step--active .step-title {
    color: var(--cur-mode-fg);
  }
  .step--error .step-title {
    color: #ef4444;
  }
  .step-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    font-size: 11px;
    font-family: ui-monospace, 'SF Mono', Menlo, monospace;
    color: var(--affine-text-secondary-color);
    background: var(--affine-hover-color, rgba(0, 0, 0, 0.04));
    border-radius: 999px;
  }
  .step-chevron {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    background: transparent;
    border: none;
    cursor: pointer;
    color: var(--affine-text-secondary-color);
    border-radius: 4px;
    padding: 0;
    transition:
      background 140ms ease,
      color 140ms ease,
      transform 180ms ease;
  }
  .step-chevron:hover {
    background: var(--cur-mode-bg);
    color: var(--cur-mode-fg);
  }
  .step-chevron svg {
    width: 12px;
    height: 12px;
  }
  .step-chevron[data-open='true'] svg {
    transform: rotate(90deg);
  }

  /* The "indented" body that sits below the title row. Vertical guide
     line on the left makes scanning easier when many steps stack. */
  .step-body {
    margin-left: 11px;
    padding-left: 18px;
    border-left: 1.5px dotted var(--affine-border-color);
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .step-substep {
    display: flex;
    gap: 6px;
    font-size: 13px;
    color: var(--affine-text-secondary-color);
    align-items: baseline;
  }
  .step-substep-text {
    animation: curie-fade 220ms ease-out;
  }
  .step-substep-detail {
    opacity: 0.72;
    font-size: 12px;
  }
  @keyframes curie-fade {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* "✓ Done · 0:03" marker on the line below the step title. */
  .step-done {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: var(--affine-text-secondary-color);
  }
  .step-done svg {
    width: 13px;
    height: 13px;
    color: #16a34a;
  }
  .step-done-meta {
    font-family: ui-monospace, 'SF Mono', Menlo, monospace;
    opacity: 0.85;
  }

  .step-error {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    font-size: 12px;
    color: #ef4444;
  }
  .step-error svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }

  /* Source chips — same as before, just smaller and matched to palette. */
  .step-sources {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    margin-top: 2px;
  }
  .source-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 10px 2px 6px;
    font-size: 11px;
    color: var(--affine-text-secondary-color);
    background: var(--affine-hover-color, rgba(0, 0, 0, 0.04));
    border: 1px solid transparent;
    border-radius: 999px;
    text-decoration: none;
    transition:
      background 140ms ease,
      color 140ms ease,
      border-color 140ms ease;
    max-width: 260px;
  }
  .source-chip:hover {
    background: var(--cur-mode-bg);
    border-color: var(--cur-mode-ring);
    color: var(--cur-mode-fg);
  }
  .source-chip-num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    font-size: 10px;
    font-weight: 600;
    font-family: ui-monospace, 'SF Mono', Menlo, monospace;
    background: var(--cur-mode-bg-strong);
    color: var(--cur-mode-fg);
    border-radius: 999px;
    line-height: 1;
    flex-shrink: 0;
  }
  .source-chip-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 210px;
  }
  .sources-toggle {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 10px;
    font-size: 11px;
    font-family: inherit;
    color: var(--cur-mode-fg);
    background: transparent;
    border: 1px dashed var(--cur-mode-ring);
    border-radius: 999px;
    cursor: pointer;
    transition:
      background 140ms ease,
      border-color 140ms ease;
  }
  .sources-toggle:hover {
    background: var(--cur-mode-soft-bg);
    border-color: var(--cur-mode-fg);
    border-style: solid;
  }
  .sources-toggle--less {
    margin-top: 8px;
    border-style: solid;
    border-color: var(--affine-border-color);
    color: var(--affine-text-secondary-color);
  }
  .sources-toggle--less:hover {
    border-color: var(--cur-mode-ring);
    color: var(--cur-mode-fg);
    background: var(--cur-mode-soft-bg);
  }
  .step-sources-expanded {
    margin-top: 4px;
    animation: curie-fade 220ms ease-out;
  }
  .step-sources-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  /* Compact identifier-only rows: [num] 42126676 (preprint) */
  .source-line {
    display: inline-flex;
    align-items: baseline;
    gap: 10px;
    padding: 3px 0;
    font-size: 12px;
    line-height: 1.4;
  }
  .source-line-num {
    font-size: 11px;
    font-weight: 600;
    font-family: ui-monospace, 'SF Mono', Menlo, monospace;
    color: var(--cur-mode-fg);
    min-width: 18px;
  }
  .source-line-link {
    color: var(--affine-text-primary-color);
    text-decoration: none;
    font-family: ui-monospace, 'SF Mono', Menlo, monospace;
    overflow-wrap: anywhere;
    border-bottom: 1px solid transparent;
    transition:
      color 140ms ease,
      border-color 140ms ease;
  }
  .source-line-link:hover {
    color: var(--cur-mode-fg);
    border-bottom-color: var(--cur-mode-ring);
  }
  .source-line-tag {
    font-size: 10px;
    padding: 1px 6px;
    background: rgba(234, 179, 8, 0.15);
    color: #b45309;
    border-radius: 999px;
  }

  /* Text-link "Show less" — no pill chrome. */
  .sources-less {
    align-self: flex-start;
    margin-top: 4px;
    padding: 0;
    background: transparent;
    border: none;
    cursor: pointer;
    font-family: inherit;
    font-size: 12px;
    color: var(--affine-text-secondary-color);
    text-decoration: underline;
    text-underline-offset: 2px;
    transition: color 140ms ease;
  }
  .sources-less:hover {
    color: var(--cur-mode-fg);
  }

  /* The live-edge spinner — small dot-ring at the bottom while the
     agent is still working. Disappears the moment the run completes. */
  .live-edge {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    margin-top: 4px;
    color: var(--cur-mode-fg);
    font-size: 12px;
    font-weight: 500;
  }
  .live-edge-spinner {
    position: relative;
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }
  .live-edge-dot {
    position: absolute;
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: var(--cur-mode-fg);
    opacity: 0.25;
    animation: curie-dot-pulse 1.1s ease-in-out infinite;
  }
  @keyframes curie-dot-pulse {
    0%,
    100% {
      opacity: 0.25;
      transform: scale(0.85);
    }
    35% {
      opacity: 1;
      transform: scale(1.1);
    }
  }

  .error-banner {
    padding: 8px 12px;
    background: rgba(239, 68, 68, 0.08);
    border-radius: 8px;
    color: #ef4444;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .error-banner svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  /* Tiny "Cancel" link tucked under the live edge — text-only,
     deliberately low key. */
  .cancel-link {
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    color: var(--affine-text-secondary-color);
    font-size: 12px;
    font-family: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
    transition: color 140ms ease;
  }
  .cancel-link:hover {
    color: var(--cur-mode-fg);
  }

  /* Collapsed-run summary — soft, scannable, no big bordered pill. */
  .collapsed-row {
    width: 100%;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 0;
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 13px;
    color: var(--affine-text-secondary-color);
    font-family: inherit;
    text-align: left;
    animation: curie-transcript-in 200ms ease-out;
    transition: color 140ms ease;
  }
  .collapsed-row:hover {
    color: var(--cur-mode-fg);
  }
  .collapsed-row .chevron {
    display: inline-flex;
    align-items: center;
    color: var(--affine-text-secondary-color);
    transition: color 140ms ease;
  }
  .collapsed-row:hover .chevron {
    color: var(--cur-mode-fg);
  }
  .collapsed-row .chevron svg {
    width: 12px;
    height: 12px;
  }
  .collapsed-row .label {
    font-weight: 500;
    color: var(--affine-text-primary-color);
  }
  .collapsed-row:hover .label {
    color: var(--cur-mode-fg);
  }
  .collapsed-row .meta {
    color: var(--affine-text-secondary-color);
    font-family: ui-monospace, 'SF Mono', Menlo, monospace;
    font-size: 11px;
  }

  @media (max-width: 560px) {
    .transcript {
      font-size: 13px;
    }
    .step-icon {
      width: 20px;
      height: 20px;
    }
    .step-body {
      padding-left: 14px;
    }
    .source-chip {
      max-width: 160px;
    }
    .source-chip-label {
      max-width: 120px;
    }
  }
`;
